import { useCallback, useEffect, useRef, useState } from "react";

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Runs an async loader on mount and whenever `deps` change.
 *
 * Guards against setting state after unmount and against a slow earlier request
 * overwriting a faster later one — both are easy to hit on the gallery, where
 * filter changes fire requests faster than they resolve.
 */
export function useAsync<T>(
  loader: () => Promise<T>,
  deps: unknown[] = [],
): AsyncState<T> & { reload: () => void; setData: (value: T) => void } {
  const [state, setState] = useState<AsyncState<T>>({ data: null, loading: true, error: null });
  const mounted = useRef(true);
  const requestId = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const run = useCallback(() => {
    const id = ++requestId.current;
    setState((previous) => ({ ...previous, loading: true, error: null }));

    loader()
      .then((data) => {
        if (!mounted.current || id !== requestId.current) return;
        setState({ data, loading: false, error: null });
      })
      .catch((error: Error) => {
        if (!mounted.current || id !== requestId.current) return;
        setState({ data: null, loading: false, error });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { run(); }, [run]);

  const setData = useCallback((value: T) => {
    setState((previous) => ({ ...previous, data: value }));
  }, []);

  return { ...state, reload: run, setData };
}
